import { CalendarClock, CalendarX } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { formatBusinessDate } from '@/lib/datetime'

import { useBusinessDay } from './use-business-day'

/** The banner every posting screen carries above its form. */
export function PostingDayNotice() {
  const query = useBusinessDay()

  if (query.isPending) {
    return <Skeleton className="h-14 w-full" />
  }

  if (query.isError || !query.data) {
    return (
      <Alert tone="warning">
        <CalendarClock aria-hidden />
        <div className="space-y-1">
          <AlertTitle>The business date could not be read</AlertTitle>
          <AlertDescription>
            The value date below has not been filled in from the ledger, so check it before
            posting. The ledger decides which day a posting lands on, and it may refuse one keyed
            here.
          </AlertDescription>
        </div>
      </Alert>
    )
  }

  const day = query.data

  if (day.businessDate === null || !day.acceptsPostings) {
    return (
      <Alert tone="danger">
        <CalendarX aria-hidden />
        <div className="space-y-1">
          <AlertTitle>
            {day.businessDate === null
              ? 'No business day is open at this branch'
              : 'This branch is not accepting postings'}
          </AlertTitle>
          <AlertDescription>
            {day.businessDate === null ? (
              <>
                Nobody has opened a day here yet, so there is no ledger date to stamp work with and
                nothing will post.
                {day.lastClosedBusinessDate ? (
                  <> The last trading day was {formatBusinessDate(day.lastClosedBusinessDate)}.</>
                ) : null}{' '}
                Open the day before taking money over the counter.
              </>
            ) : (
              <>
                The day is {day.status === 'Closing' ? 'closing' : day.status.toLowerCase()}, and
                the ledger will refuse a posting into it. Nothing keyed below will be accepted.
              </>
            )}
          </AlertDescription>
        </div>
      </Alert>
    )
  }

  return (
    <Alert tone="info">
      <CalendarClock aria-hidden />
      <div className="space-y-1">
        <AlertTitle>Posting into {formatBusinessDate(day.businessDate)}</AlertTitle>
        <AlertDescription>
          The branch&apos;s business date, not today&apos;s. It is what the value date below is
          filled in from.
        </AlertDescription>
      </div>
    </Alert>
  )
}
